// traceInfluence.js — the trace's provenance graph projected onto the LANES
// grid, so a link stops being an abstract node pair and becomes two cells the
// reader can already see: "the write at T477 in build_deck.js was fed by the
// page fetched at T31".
//
// The graph itself is not computed here. provenanceOverOps answers "what fed
// this" in evidence tiers that say how hard the claim is (cited / quoted /
// entity, then consultation for a research or query op that preceded a
// production with no textual transfer). What that answer lacks is a POSITION,
// which is exactly what a lane item already has.
//
// So the graph is built over the lanes model's items rather than re-extracted
// from the turns. Every door into the model produces the same packet - an op
// on a lane at a turn: the committed extractors, a code-mode cell's virtual
// blocks, the shell grammar's reads, a verified reading. Re-extracting saw
// only the first of those, which left a Codex session, whose reads all arrive
// through the shell, with no sources to link an edit back to. Built over the
// items, an edge between two packets IS an edge between two cells, and a new
// door links the moment it puts items on a lane. Ops the pass leaves out (its
// cap keeps the newest) are counted in `stats.dropped` rather than quietly
// thinning the graph.
//
// Pure and DOM-free: geometry (which pixel a cell sits at) belongs to the view.

import { provenanceOverOps } from "./traceAnatomy.js";

// Strongest claim first — the tier kept when parallel edges between one pair of
// cells collapse into a single drawn link.
const TIER_RANK = { cited: 0, quoted: 1, entity: 2, adjacent: 3 };

export const cellKey = (laneId, turn) => `${laneId}@${turn}`;

/**
 * Project one trace's provenance edges onto its lane cells.
 *
 * @param {{lanes: Array}} model  buildTraceLanes output, extra items included
 * @returns {{
 *   links: Array<{from, to, tier, consultation, count, evidence: string[]}>,
 *   bySource: Map<string, Array>, byTarget: Map<string, Array>,
 *   stats: { edges, links, unplaced, dropped }
 * }}  from/to are { laneId, turn }; the maps key on cellKey.
 */
export function buildTraceInfluence(model) {
  const placed = [];
  for (const lane of model?.lanes || []) {
    for (const it of lane.items || []) {
      if (it.turnIndex == null || !it.op || !it.id) continue;
      placed.push({ it, cell: { laneId: lane.id, turn: it.turnIndex } });
    }
  }
  // Authored order - turn, then block - is what provenance reads as time. The
  // sort is stable, so ops sharing a block keep the order they were emitted in:
  // the reads of one batched command stay in command order.
  placed.sort((a, b) => a.it.turnIndex - b.it.turnIndex || (a.it.blockIndex ?? 0) - (b.it.blockIndex ?? 0));
  const cellOf = new Map();
  const rankOf = new Map();
  const byDomain = new Map();
  for (const { it, cell } of placed) {
    // First writer wins: one item id is one op, so a second lane holding it is
    // the same cell seen twice.
    if (cellOf.has(it.id)) continue;
    cellOf.set(it.id, cell);
    rankOf.set(it.id, rankOf.size + 1);
    // Keyed by the ITEM id, which is unique across doors: an extractor's op key
    // and the shell grammar's `<call>#<n>` can both name one block, and two
    // doors reading one call must stay two nodes.
    const list = byDomain.get(it.domain) || [];
    list.push({ ...it.op, key: it.id });
    byDomain.set(it.domain, list);
  }
  // Within one trace the session/prompt identity only has to be non-empty and
  // consistent: buildProvenance drops nodes with no session, and pairs edges
  // inside one session, which is exactly the scope wanted here.
  const built = provenanceOverOps(
    [...byDomain],
    { sessionId: "trace", promptId: "trace", endpointId: "", ts: 0 },
    (key) => rankOf.get(key),
  );
  const locate = (node) => (node?.key ? cellOf.get(node.key) || null : null);

  const merged = new Map();
  let unplaced = 0;
  for (const e of built.edges || []) {
    const from = locate(e.source);
    const to = locate(e.target);
    if (!from || !to) {
      unplaced += 1;
      continue;
    }
    // A cell feeding itself is not a link — two ops on one subject in one turn
    // are one mark, and an arrow from a mark to itself says nothing.
    if (from.laneId === to.laneId && from.turn === to.turn) continue;
    const key = `${cellKey(from.laneId, from.turn)}|${cellKey(to.laneId, to.turn)}`;
    const cur = merged.get(key);
    if (cur) {
      cur.count += 1;
      if ((TIER_RANK[e.tier] ?? 9) < (TIER_RANK[cur.tier] ?? 9)) {
        cur.tier = e.tier;
        cur.consultation = e.class === "consultation";
        cur.evidence = evidenceOf(e);
      }
      continue;
    }
    merged.set(key, {
      from,
      to,
      tier: e.tier,
      consultation: e.class === "consultation",
      count: 1,
      evidence: evidenceOf(e),
    });
  }

  // Strongest last so the hardest claims draw on top of the consultations.
  const links = [...merged.values()].sort((a, b) => (TIER_RANK[b.tier] ?? 9) - (TIER_RANK[a.tier] ?? 9));
  const bySource = new Map();
  const byTarget = new Map();
  for (const link of links) {
    push(bySource, cellKey(link.from.laneId, link.from.turn), link);
    push(byTarget, cellKey(link.to.laneId, link.to.turn), link);
  }
  return {
    links,
    bySource,
    byTarget,
    stats: { edges: (built.edges || []).length, links: links.length, unplaced, dropped: built.droppedOps || 0 },
  };
}

// What the tier actually matched on, kept as the LIST the pass produced rather
// than its first item: the entity tier's claim is "these rare tokens are shared",
// and one of five tokens understates it.
const EVIDENCE_CAP = 3;

function evidenceOf(e) {
  const list = Array.isArray(e.evidence) ? e.evidence : [e.evidence];
  return list.filter((s) => typeof s === "string" && s).slice(0, EVIDENCE_CAP);
}

function push(map, key, value) {
  const arr = map.get(key);
  if (arr) arr.push(value);
  else map.set(key, [value]);
}

/**
 * The links touching a focus — one cell, or one stretch of the turn axis.
 *
 * `laneId` null focuses the TURNS themselves: every link that enters or leaves
 * the range, whichever subject it landed on. The range is inclusive on both
 * ends because a mark at low zoom covers a bucket of turns, not one.
 *
 * @returns {{ incoming: Array, outgoing: Array }} incoming fed the focus;
 *   outgoing is what the focus fed. A link inside the focus on both ends
 *   (one turn bucket, two lanes) counts as outgoing only, so it draws once.
 */
export function linksAt(influence, { laneId = null, laneIds = null, fromTurn, toTurn }) {
  const incoming = [];
  const outgoing = [];
  // laneIds widens the focus to a folded GROUP: a head-row mark merges many
  // constituent lanes, and hovering it should light every edge touching any
  // of them at these turns.
  const laneSet = laneIds ? new Set(laneIds) : null;
  const inFocus = (end) => end.turn >= fromTurn && end.turn <= toTurn
    && (laneSet ? laneSet.has(end.laneId) : (laneId == null || end.laneId === laneId));
  for (const link of influence?.links || []) {
    if (inFocus(link.from)) outgoing.push(link);
    else if (inFocus(link.to)) incoming.push(link);
  }
  return { incoming, outgoing };
}
